// BlockAttachmentInset.tsx — "Tower Attachment Details & Forces" depiction
// (Brian 2026-09-08, styled after the reference tool's attachment view — our
// own drawing, not a copy). Shows how the block hangs on the tower face and
// resolves the sling force into tower components:
//   1-sling: sling + shackle + block on ONE leg at a panel point;
//            F_SL carried by the sling, resolved at the load-line angle θ:
//            F_y = F_SL·cos θ (vertical), F_z = F_SL·sin θ (horizontal pull).
//   face basket (2 slings): block mid-face on 2 legs at panel points;
//            α DERIVED = asin(faceWidth / (2·slingLength)), never typed;
//            per-leg T = F_SL / (2·cos α); F_y = F_SL/2 per leg, F_z = T·sin α.
//            Refuse α > 60° from vertical; warn > 45°.
import { solveFaceBasket } from '../../utils/riggingCalculations.v2';

interface BlockAttachmentInsetProps {
  /** resultant force at the block (top block resultant from the engine) */
  blockForceLbs: number;
  /** load-line angle from vertical (θ) — resolves the 1-sling case */
  thetaDeg: number;
  config: '1-leg' | 'faceBasket2';
  faceWidthFt: number;
  slingLengthFt: number;
}

const BLUE = '#1a237e';

function Leg({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill="#e8e8e8" stroke="black" strokeWidth={1.25} />
      <line x1={x} y1={y + h * 0.35} x2={x + w} y2={y + h * 0.35} stroke="black" strokeWidth={0.75} />
      <line x1={x} y1={y + h * 0.7} x2={x + w} y2={y + h * 0.7} stroke="black" strokeWidth={0.75} />
    </g>
  );
}

function Arrow({ x1, y1, x2, y2, color }: { x1: number; y1: number; x2: number; y2: number; color: string }) {
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const hx = x2 - 7 * Math.cos(ang);
  const hy = y2 - 7 * Math.sin(ang);
  const px = 3.2 * Math.cos(ang + Math.PI / 2);
  const py = 3.2 * Math.sin(ang + Math.PI / 2);
  return (
    <g stroke={color} fill={color}>
      <line x1={x1} y1={y1} x2={hx} y2={hy} strokeWidth={1.5} />
      <polygon points={`${x2},${y2} ${hx + px},${hy + py} ${hx - px},${hy - py}`} stroke="none" />
    </g>
  );
}

export function BlockAttachmentInset({ blockForceLbs, thetaDeg, config, faceWidthFt, slingLengthFt }: BlockAttachmentInsetProps) {
  if (config === '1-leg') {
    // ---- single sling on one leg -------------------------------------------
    const fSL = blockForceLbs;
    const rad = (thetaDeg * Math.PI) / 180;
    const fy = fSL * Math.cos(rad);
    const fz = fSL * Math.sin(rad);
    const W = 320;
    const H = 230;
    const legX = 60;
    const blockX = legX + 46;
    const blockY = 128;
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="block-attachment-inset">
        <p className="text-sm font-semibold text-gray-900 mb-1">Block Attachment — Single Sling (1 leg at panel point)</p>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="border border-gray-100 rounded" role="img" aria-label="Single sling block attachment">
          {/* tower face: two legs + bracing (top-down face view) */}
          <Leg x={legX} y={18} w={26} h={190} />
          <Leg x={legX + 150} y={18} w={26} h={190} />
          <line x1={legX + 26} y1={60} x2={legX + 150} y2={110} stroke="#bbb" strokeWidth={2} />
          <line x1={legX + 150} y1={60} x2={legX + 26} y2={110} stroke="#bbb" strokeWidth={2} />
          {/* sling wraps the near leg down to the shackle + block */}
          <line x1={legX + 13} y1={70} x2={blockX} y2={blockY - 14} stroke="black" strokeWidth={2} />
          {/* shackle */}
          <circle cx={blockX} cy={blockY - 8} r={5} fill="none" stroke="black" strokeWidth={1.75} />
          {/* traveling block */}
          <rect x={blockX - 11} y={blockY - 2} width={22} height={26} fill="#f4f4f4" stroke="black" strokeWidth={1.5} />
          <circle cx={blockX} cy={blockY + 11} r={5} fill="none" stroke="black" strokeWidth={1.25} />
          {/* load line hanging below */}
          <line x1={blockX} y1={blockY + 26} x2={blockX} y2={H - 14} stroke="black" strokeWidth={1.25} />
          {/* force arrows at the block */}
          <Arrow x1={blockX} y1={blockY - 14} x2={legX + 15} y2={72} color={BLUE} />
          <text x={blockX + 20} y={blockY - 46} fontSize={10} fontFamily="monospace" fill={BLUE}>F_SL {fSL.toFixed(2)} lb</text>
          <Arrow x1={blockX + 14} y1={blockY + 30} x2={blockX + 14} y2={blockY + 66} color={BLUE} />
          <text x={blockX + 20} y={blockY + 56} fontSize={10} fontFamily="monospace" fill={BLUE}>F_y {fy.toFixed(2)} lb</text>
          <Arrow x1={blockX - 14} y1={blockY + 34} x2={blockX - 14 - Math.min(40, Math.max(18, fz * 0.35))} y2={blockY + 34} color={BLUE} />
          <text x={6} y={blockY + 52} fontSize={10} fontFamily="monospace" fill={BLUE}>F_z {fz.toFixed(2)} lb</text>
          <text x={legX + 96} y={H - 4} fontSize={9} fontFamily="monospace">TO TOWER FACE</text>
          <text x={8} y={14} fontSize={9} fontFamily="monospace">LEG (PANEL POINT)</text>
        </svg>
        <p className="text-xs text-gray-600 mt-2">
          F_SL = block resultant {fSL.toFixed(1)} lb · resolved at load-line angle θ = {thetaDeg.toFixed(2)}° from vertical:
          F_y = F_SL·cos θ = {fy.toFixed(2)} lb (vertical on the leg) · F_z = F_SL·sin θ = {fz.toFixed(2)} lb (horizontal pull).
        </p>
      </div>
    );
  }

  // ---- face basket: 2 slings to 2 legs at panel points ----------------------
  const sol = solveFaceBasket(blockForceLbs, faceWidthFt, slingLengthFt);
  const refused = sol.refusal;
  const alpha = sol.alphaDeg;
  const legT = refused ? NaN : sol.legLbs;
  const fyLeg = refused ? NaN : blockForceLbs / 2;
  const fzLeg = refused ? NaN : legT * Math.sin((alpha * Math.PI) / 180);
  const W = 320;
  const H = 240;
  const legAx = 62;
  const legBx = 232;
  const blockX = (legAx + legBx) / 2;
  const blockY = 150;
  const panelY = 66;
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="block-attachment-inset">
      <p className="text-sm font-semibold text-gray-900 mb-1">Block Attachment — Face Basket (2 slings to 2 legs at panel points)</p>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="border border-gray-100 rounded" role="img" aria-label="Face basket block attachment">
        <Leg x={legAx} y={18} w={26} h={200} />
        <Leg x={legBx} y={18} w={26} h={200} />
        <line x1={legAx + 26} y1={96} x2={legBx} y2={140} stroke="#bbb" strokeWidth={2} />
        <line x1={legBx} y1={96} x2={legAx + 26} y2={140} stroke="#bbb" strokeWidth={2} />
        {/* panel point marks + shackles */}
        <circle cx={legAx + 13} cy={panelY} r={4} fill="black" />
        <circle cx={legBx + 13} cy={panelY} r={4} fill="black" />
        {/* the two slings down to the block */}
        <line x1={legAx + 13} y1={panelY} x2={blockX} y2={blockY - 12} stroke="black" strokeWidth={2} />
        <line x1={legBx + 13} y1={panelY} x2={blockX} y2={blockY - 12} stroke="black" strokeWidth={2} />
        {/* block */}
        <rect x={blockX - 11} y={blockY - 2} width={22} height={26} fill="#f4f4f4" stroke="black" strokeWidth={1.5} />
        <circle cx={blockX} cy={blockY + 11} r={5} fill="none" stroke="black" strokeWidth={1.25} />
        <line x1={blockX} y1={blockY + 26} x2={blockX} y2={H - 16} stroke="black" strokeWidth={1.25} />
        {/* α arc at the block (from vertical) */}
        {!refused && (
          <g>
            <line x1={blockX} y1={blockY - 12} x2={blockX} y2={blockY - 44} stroke="#999" strokeWidth={0.75} strokeDasharray="3 3" />
            <text x={blockX + 10} y={blockY - 48} fontSize={9} fontFamily="monospace">α = {alpha.toFixed(2)}°</text>
          </g>
        )}
        {/* face width dimension (blue, like the reference) */}
        <line x1={legAx + 13} y1={panelY - 22} x2={legBx + 13} y2={panelY - 22} stroke={BLUE} strokeWidth={1} />
        <line x1={legAx + 13} y1={panelY - 28} x2={legAx + 13} y2={panelY - 16} stroke={BLUE} strokeWidth={1} />
        <line x1={legBx + 13} y1={panelY - 28} x2={legBx + 13} y2={panelY - 16} stroke={BLUE} strokeWidth={1} />
        <text x={blockX - 44} y={panelY - 28} fontSize={9} fontFamily="monospace" fill={BLUE}>FACE WIDTH {faceWidthFt} FT</text>
        {/* per-leg force arrows */}
        {!refused && (
          <g>
            <Arrow x1={blockX - 16} y1={blockY + 6} x2={blockX - 16} y2={blockY + 40} color={BLUE} />
            <text x={blockX - 132} y={blockY + 34} fontSize={9.5} fontFamily="monospace" fill={BLUE}>F_y {fyLeg.toFixed(2)} lb /leg</text>
            <Arrow x1={blockX + 16} y1={blockY + 6} x2={blockX + 16 + 26} y2={blockY + 6} color={BLUE} />
            <text x={blockX + 16} y={blockY - 2} fontSize={9.5} fontFamily="monospace" fill={BLUE}>F_z {fzLeg.toFixed(2)} lb /leg</text>
            <text x={12} y={H - 4} fontSize={9.5} fontFamily="monospace" fill={BLUE}>F_SL = {legT.toFixed(2)} lb per sling</text>
          </g>
        )}
      </svg>
      {refused ? (
        <p className="text-sm text-red-700 font-semibold mt-2" data-testid="face-basket-refusal">
          REFUSED ({refused.rule}): {refused.message}
        </p>
      ) : (
        <p className="text-xs text-gray-600 mt-2">
          α = asin({faceWidthFt} / (2 × {slingLengthFt})) = {alpha.toFixed(2)}° from vertical ·
          F_SL = {blockForceLbs.toFixed(1)} / (2·cos α) = {legT.toFixed(2)} lb per sling ·
          F_y = {fyLeg.toFixed(2)} lb per leg (vertical) · F_z = {fzLeg.toFixed(2)} lb per leg (horizontal, toward face center).
          {sol.warn ? ' WARNING: α exceeds 45° — consider longer slings or narrower face spacing.' : ''}
        </p>
      )}
    </div>
  );
}
